// Each page's <head> tags for search engines and link previews: title, description, canonical
// URL, Open Graph / Twitter tags and schema.org structured data (JSON-LD).
// App.vue calls usePageMeta once; the defaults come from the route (title from App.vue,
// description from src/router/pageDescriptions.js). A page with its own content (a news item,
// a video) overrides them with setPageMeta(route.path, {title, description, image, …}).

import {reactive, watchEffect} from "vue";
import {useRoute} from "vue-router";
import {getPageDescription} from "@/router/pageDescriptions";

const SITE_URL = "https://vizbi.org";
const SITE_NAME = "VIZBI";
const MAX_DESCRIPTION_LENGTH = 160;
export const ORGANIZATION_ID = `${SITE_URL}/#organization`;

const ORGANIZATION = {
  "@type": "Organization",
  "@id": ORGANIZATION_ID,
  name: "VIZBI: Visualizing Biological Data",
  alternateName: SITE_NAME,
  url: SITE_URL,
  logo: `${SITE_URL}/images/logo/vizbi-inline.png`
};

const WEBSITE = {
  "@type": "WebSite",
  "@id": `${SITE_URL}/#website`,
  name: SITE_NAME,
  url: SITE_URL,
  publisher: {"@id": ORGANIZATION_ID}
};

// path → that page's own meta; kept so a kept-alive page gets its meta back on a revisit
const pageMetaByPath = reactive(new Map());

// empty fields (e.g. a video without a description) keep the route's defaults
export function setPageMeta(path, meta) {
  const filledFields = Object.entries(meta).filter(([, value]) => value);
  pageMetaByPath.set(path, Object.fromEntries(filledFields));
}

export function getPageUrl(path) {
  return `${SITE_URL}${path.replace(/\/+$/, "")}`;
}

// plain text cut at a word boundary, for <meta name="description">
export function getShortDescription(text) {
  const plainText = String(text || "").replace(/\s+/g, " ").trim();
  if (plainText.length <= MAX_DESCRIPTION_LENGTH) return plainText;
  const cut = plainText.slice(0, MAX_DESCRIPTION_LENGTH - 1);
  return `${cut.slice(0, cut.lastIndexOf(" "))}…`;
}

function setMetaTag(attribute, key, content) {
  let tag = document.head.querySelector(`meta[${attribute}="${key}"]`);
  if (!content) return tag?.remove();
  if (!tag) {
    tag = document.createElement("meta");
    tag.setAttribute(attribute, key);
    document.head.append(tag);
  }
  tag.setAttribute("content", content);
}

function setCanonicalLink(url) {
  let link = document.head.querySelector("link[rel='canonical']");
  if (!link) {
    link = document.createElement("link");
    link.rel = "canonical";
    document.head.append(link);
  }
  link.href = url;
}

function setStructuredData(pageData = []) {
  let script = document.head.querySelector("script#structured-data");
  if (!script) {
    script = document.createElement("script");
    script.id = "structured-data";
    script.type = "application/ld+json";
    document.head.append(script);
  }
  const graph = [ORGANIZATION, WEBSITE, ...pageData];
  script.textContent = JSON.stringify({"@context": "https://schema.org", "@graph": graph});
}

function applyPageMeta({title, description, url, image, type, structuredData}) {
  document.title = title;
  setMetaTag("name", "description", description);
  setCanonicalLink(url);
  setMetaTag("property", "og:site_name", SITE_NAME);
  setMetaTag("property", "og:type", type || "website");
  setMetaTag("property", "og:title", title);
  setMetaTag("property", "og:description", description);
  setMetaTag("property", "og:url", url);
  setMetaTag("property", "og:image", image);
  setMetaTag("name", "twitter:card", image ? "summary_large_image" : "summary");
  setStructuredData(structuredData);
}

export function usePageMeta(getTitle) {
  const route = useRoute();
  watchEffect(() => {
    if (!route.name) return;
    const defaults = {title: getTitle(route), description: getPageDescription(route)};
    const pageMeta = pageMetaByPath.get(route.path) || {};
    applyPageMeta({...defaults, ...pageMeta, url: getPageUrl(route.path)});
  });
}
